import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, ArrowLeft, MagnifyingGlass, ChatCircleText, Tag } from '@phosphor-icons/react';
import { useAvailableMBs } from './MBSetupBadge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { maskPhone, canViewFullPhone } from '../utils/phoneMask';

const BadgeDetailsView = ({ badgeType, API, onClose, onOpenChat }) => {
  const { user } = useAuth();
  const hasFullPhoneAccess = canViewFullPhone(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const availableMBs = useAvailableMBs();
  const assignedMBs = Array.isArray(user?.assigned_mbs) ? user.assigned_mbs : [];
  const badgeMBs = user?.role === 'deposit_handler' && !assignedMBs.some((mb) => String(mb).toUpperCase() === 'ALL')
    ? availableMBs.filter((mb) => assignedMBs.some((assigned) => String(assigned).toUpperCase() === String(mb).toUpperCase()))
    : availableMBs;

  const [minThreshold, setMinThreshold] = useState(0);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      if (badgeType === 'pt') {
        const thresholdResponse = await axios.get(`${API}/settings/pt-threshold`);
        setMinThreshold(Number(thresholdResponse.data?.minutes) || 0);
      }
      const url = badgeType === 'pt' ? `${API}/chat/badge-details/pt` : `${API}/chat/badge-details/${badgeType}`;
      const res = await axios.get(url);
      setItems(res.data || []);
    } catch (err) {
      console.error('Failed to fetch badge details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [badgeType]);

  const getTitle = () => {
    if (badgeType === 'pw') return 'Pending Withdrawals (>30 min)';
    if (badgeType === 'n') return 'New Client — Unassigned Members';
    if (badgeType === 'pt') return `Pending Tasks (Staff Reply Needed ${minThreshold > 0 ? `— ${minThreshold}m+` : ''})`;
    return 'Details';
  };

  const handleAssignMB = async (customerId, mbValue) => {
    if (!mbValue) return;
    try {
      await axios.put(`${API}/clients/${customerId}/mb-setup`, { mb_setup: mbValue });
      toast.success(`Assigned to ${mbValue}`);
      // Optimistically remove assigned member from list
      setItems(prev => prev.filter(item => item.customer_id !== customerId));
    } catch (err) {
      toast.error('Failed to assign MB group');
    }
  };

  const filteredItems = items.filter(item => {
    if (badgeType === 'n' && (item.phone?.startsWith('GUEST_') || item.is_guest)) return false;
    if (badgeType === 'pt' && (item.elapsed_minutes || 0) < minThreshold) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const nameMatch = (item.customer_name || '').toLowerCase().includes(q);
    const phoneMatch = (item.phone || '').toLowerCase().includes(q);
    const msgMatch = (item.last_message_text || '').toLowerCase().includes(q);
    return nameMatch || phoneMatch || msgMatch;
  });

  const formatElapsed = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs} hr ${mins} min`;
  };

  return (
    <div className="fixed inset-0 bg-wa-hover z-[700] flex flex-col h-screen overflow-hidden">
      {/* Dark Green Header */}
      <div className="bg-[#005e54] text-white px-4 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
            <ArrowLeft size={20} weight="bold" />
          </button>
          <div>
            <h2 className="text-base font-bold tracking-wide">{getTitle()}</h2>
            <p className="text-xs text-green-100/80">
              {badgeType === 'n' ? `Unassigned members: ${filteredItems.length}` : badgeType === 'pt' ? `Pending tasks (${minThreshold}m+): ${filteredItems.length}` : `Total items: ${filteredItems.length}`}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
          <X size={20} weight="bold" />
        </button>
      </div>

      {/* Filter and search row */}
      <div className="bg-wa-panel border-b border-wa-border p-3 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-2.5 text-wa-text-secondary">
            <MagnifyingGlass size={16} />
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by client name or phone..."
            className="w-full pl-9 pr-4 py-1.5 bg-wa-hover border border-wa-border rounded-lg text-sm text-wa-text-primary focus:outline-none focus:ring-1 focus:ring-[#005e54]"
          />
        </div>
        <button
          onClick={fetchDetails}
          className="px-3 py-1.5 bg-wa-hover border border-wa-border hover:bg-wa-panel rounded-lg text-xs font-semibold text-wa-text-primary transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto bg-wa-panel p-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 text-wa-text-secondary text-sm">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005e54] mb-2"></div>
            Loading details...
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-wa-text-secondary text-sm italic">
            No unassigned members found.
          </div>
        ) : (
          <div className="overflow-x-auto border border-wa-border rounded-xl shadow-sm bg-wa-panel">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-[#004d40] text-white uppercase tracking-wider font-bold text-left">
                  <th className="px-4 py-3">Client / Username</th>
                  {badgeType === 'n' ? (
                    <>
                      <th className="px-4 py-3">Phone</th>
                      <th className="px-4 py-3">Registered Date</th>
                      <th className="px-4 py-3 text-right pr-6">Assign MB Group</th>
                    </>
                  ) : (
                    <>
                      {badgeType === 'pt' && <th className="px-4 py-3">Type</th>}
                      <th className="px-4 py-3">Last Message</th>
                      <th className="px-4 py-3">Waiting Time</th>
                      <th className="px-4 py-3 text-center">Action</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-wa-border text-wa-text-primary">
                {filteredItems.map((item, idx) => (
                  <tr key={item.customer_id || item.id || idx} className="hover:bg-wa-hover/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-wa-text-primary">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 font-bold text-[10px] flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <span>{item.customer_name}</span>
                        {item.mb_setup && (
                          <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-[9px] font-black rounded border border-amber-200 dark:border-amber-700/50">
                            {item.mb_setup}
                          </span>
                        )}
                        {item.is_new_today && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-[9px] font-bold rounded-full">New Today</span>
                        )}
                      </div>
                    </td>
                    {badgeType === 'n' ? (
                      <>
                        <td className="px-4 py-3 font-mono text-wa-text-secondary">{maskPhone(item.phone, hasFullPhoneAccess)}</td>
                        <td className="px-4 py-3 text-wa-text-secondary">
                          {new Date(item.created_at).toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </td>
                        <td className="px-4 py-3 text-right pr-6">
                          <select
                            key={`select-${item.customer_id || idx}`}
                            value=""
                            onChange={(e) => handleAssignMB(item.customer_id, e.target.value)}
                            className="px-2.5 py-1 bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold text-xs rounded-full border border-amber-300 cursor-pointer outline-none"
                          >
                            <option value="" disabled>🏷️ Assign MB ▾</option>
                            {badgeMBs.map(mb => (
                              <option key={mb} value={mb}>{mb}</option>
                            ))}
                          </select>
                        </td>
                      </>
                    ) : (
                      <>
                        {badgeType === 'pt' && (
                          <td className="px-4 py-3 capitalize font-bold">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                              item.chat_type === 'deposit' ? 'bg-green-100 text-green-800' :
                              item.chat_type === 'withdrawal' ? 'bg-orange-100 text-orange-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {item.chat_type}
                            </span>
                          </td>
                        )}
                        <td className="px-4 py-3 max-w-xs truncate text-wa-text-secondary italic">
                          {item.last_message_text}
                        </td>
                        <td className="px-4 py-3 font-bold text-red-500">
                          {formatElapsed(item.elapsed_minutes)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => {
                              onOpenChat(item.room_id);
                              onClose();
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-[10px] transition-colors"
                          >
                            <ChatCircleText size={14} weight="bold" />
                            Open Chat
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeDetailsView;
